import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { CompanyLineageSection } from './CompanyLineageSection';
import { companyProfileHooks, contactHooks } from './hooks';
// Radix Select.Item disallows an empty-string value (it reserves "" for
// the cleared/placeholder state), so we omit a "None" row and let the
// placeholder serve as the unselected state.
const socioeconomicOptions = [
    {
        value: 'SDB',
        label: 'Small Disadvantaged Business'
    },
    {
        value: 'WOSB',
        label: 'Woman-Owned Small Business'
    },
    {
        value: 'HUBZone',
        label: 'HUBZone'
    },
    {
        value: '8(a)',
        label: '8(a)'
    },
    {
        value: 'SDVOSB',
        label: 'Service-Disabled Veteran-Owned'
    },
    {
        value: 'VOSB',
        label: 'Veteran-Owned'
    }
];
const baseColumns = [
    {
        key: 'name',
        header: 'Name',
        render: (row)=>/*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-2",
                children: [
                    row.name,
                    row.isOwnCompany && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        children: "Your organization"
                    })
                ]
            })
    },
    {
        key: 'cageCode',
        header: 'CAGE'
    },
    {
        key: 'uei',
        header: 'UEI'
    },
    {
        key: 'socioeconomicStatus',
        header: 'Socioeconomic'
    }
];
const fields = [
    {
        kind: 'text',
        name: 'name',
        label: 'Company name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'text',
        name: 'cageCode',
        label: 'CAGE Code',
        maxLength: 20
    },
    {
        kind: 'text',
        name: 'dunsNumber',
        label: 'DUNS Number',
        maxLength: 20
    },
    {
        kind: 'text',
        name: 'uei',
        label: 'UEI',
        maxLength: 20
    },
    {
        kind: 'select',
        name: 'socioeconomicStatus',
        label: 'Socioeconomic status',
        options: socioeconomicOptions
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const companyDefaults = (c)=>({
        id: c?.id ?? '',
        name: c?.name ?? '',
        cageCode: c?.cageCode ?? '',
        dunsNumber: c?.dunsNumber ?? '',
        uei: c?.uei ?? '',
        socioeconomicStatus: c?.socioeconomicStatus ?? ''
    });
// Read sections plus, per row, the inline-edit control. Every field the create
// form exposes carries an `editField` (the SAME config), so edit-in-place and
// create stay in lockstep.
function companyDetailSections(c) {
    return [
        {
            heading: 'Profile',
            rows: [
                {
                    label: 'Company name',
                    value: c.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Company name',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'CAGE Code',
                    value: c.cageCode || '—',
                    editField: {
                        kind: 'text',
                        name: 'cageCode',
                        label: 'CAGE Code',
                        maxLength: 20
                    }
                },
                {
                    label: 'DUNS Number',
                    value: c.dunsNumber || '—',
                    editField: {
                        kind: 'text',
                        name: 'dunsNumber',
                        label: 'DUNS Number',
                        maxLength: 20
                    }
                },
                {
                    label: 'UEI',
                    value: c.uei || '—',
                    editField: {
                        kind: 'text',
                        name: 'uei',
                        label: 'UEI',
                        maxLength: 20
                    }
                },
                {
                    label: 'Socioeconomic status',
                    value: c.socioeconomicStatus || '—',
                    editField: {
                        kind: 'select',
                        name: 'socioeconomicStatus',
                        label: 'Socioeconomic status',
                        options: socioeconomicOptions
                    }
                }
            ]
        }
    ];
}
// ListCompanyProfiles returns the whole tenant registry with no limit/offset,
// and EntityTable has no virtualization — mounting all of it is a
// multi-second main-thread stall once a tenant has thousands of companies
// (the RPC itself stays fast at that volume; the render is what stalls,
// SAD-323). 200 matches the existing `limit: 200` convention in hooks.ts.
// This caps what gets MOUNTED, not what gets fetched — the fetch, and its
// shape, are unchanged.
const REGISTRY_ROW_CAP = 200;
export function CompaniesTab() {
    const tenantId = useTenantId();
    const { data, isLoading } = companyProfileHooks.useList({
        companyId: tenantId
    });
    const upsert = companyProfileHooks.useUpsert();
    const remove = companyProfileHooks.useDelete();
    const items = data?.items ?? [];
    // See REGISTRY_ROW_CAP above — only the rows handed to EntityTable are
    // capped; every other use of `items` (detail lookup, lineage picker) keeps
    // the full fetched list.
    const visibleItems = useMemo(()=>items.slice(0, REGISTRY_ROW_CAP), [
        items
    ]);
    // People (spec 10 / Phase 3 slice 1): "you can record which company a
    // person belongs to; you cannot ask a company who its people are." The
    // count column + the drawer's People section are the reverse read —
    // client-side join over the tenant contact list (already unpaged; the
    // affiliation write path is unchanged).
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const contactsByCompany = useMemo(()=>{
        const m = new Map();
        for (const c of contactsQuery.data?.items ?? []){
            if (!c.companyProfileId) continue;
            const list = m.get(c.companyProfileId) ?? [];
            list.push({
                id: c.id,
                name: `${c.firstName} ${c.lastName}`.trim() || c.email || 'Unnamed contact',
                title: c.title,
                email: c.email
            });
            m.set(c.companyProfileId, list);
        }
        return m;
    }, [
        contactsQuery.data
    ]);
    const columns = useMemo(()=>[
            ...baseColumns,
            {
                key: 'contacts',
                header: 'Contacts',
                // Row click opens the drawer whose People section IS the list.
                render: (row)=>/*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.deliveryRegistries.companyContactsCount,
                        children: contactsByCompany.get(row.id)?.length ?? 0
                    })
            }
        ], [
        contactsByCompany
    ]);
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state (which infinite-loops on fresh-but-
    // equal references and hangs the runner).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create).
    const submitCompany = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name,
            cageCode: values.cageCode ?? '',
            dunsNumber: values.dunsNumber ?? '',
            uei: values.uei ?? '',
            socioeconomicStatus: values.socioeconomicStatus ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: companyDefaults(detail),
        onSubmit: (v)=>submitCompany(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    "data-testid": Testids.deliveryRegistries.companyCreate,
                    children: "New Company"
                })
            }),
            !isLoading && items.length > REGISTRY_ROW_CAP && /*#__PURE__*/ _jsxs("p", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Showing the first ",
                    REGISTRY_ROW_CAP.toLocaleString(),
                    " of ",
                    items.length.toLocaleString(),
                    ' ',
                    items.length === 1 ? 'company' : 'companies',
                    "."
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: visibleItems,
                columns: columns,
                testid: Testids.deliveryRegistries.companiesTable,
                rowTestid: Testids.deliveryRegistries.companyRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRegistries.companyEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRegistries.companyDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Company",
                fields: fields,
                defaultValues: companyDefaults(null),
                onSubmit: async (v)=>{
                    await submitCompany(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Company' : '',
                sections: detail ? companyDetailSections(detail) : [],
                testid: Testids.deliveryRegistries.companyDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.companyPeopleSection,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "People"
                            }),
                            (detail ? contactsByCompany.get(detail.id) ?? [] : []).map((p)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-medium",
                                            children: p.name
                                        }),
                                        (p.title || p.email) && /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: [
                                                p.title,
                                                p.email
                                            ].filter(Boolean).join(' · ')
                                        })
                                    ]
                                }, p.id)),
                            detail && (contactsByCompany.get(detail.id) ?? []).length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No contacts affiliated with this company yet — set a contact's company in Registries → Contacts."
                            })
                        ]
                    }),
                    detail ? /*#__PURE__*/ _jsx(CompanyLineageSection, {
                        companies: items.map((c)=>({
                                id: c.id,
                                name: c.name
                            })),
                        companyProfileId: detail.id
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Company",
                description: deleting ? `Remove "${deleting.name}" from the companies directory? It will be deactivated and hidden from lists; an administrator can restore it.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
